import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import { useAddAdmin, useAdmins, useRemoveAdmin } from '../../api/queries';

export default function AdminAdmins() {
  const { data, isLoading, error } = useAdmins();
  const addAdmin = useAddAdmin();
  const removeAdmin = useRemoveAdmin();
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = useState('');

  if (isLoading) return <CircularProgress />;
  if (error) return <Alert severity="error">Failed: {String(error)}</Alert>;

  const add = async () => {
    if (!email.trim()) return;
    try {
      await addAdmin.mutateAsync(email.trim().toLowerCase());
      enqueueSnackbar(`${email.trim()} is now an admin`, { variant: 'success' });
      setEmail('');
    } catch (err) {
      enqueueSnackbar(`Failed: ${err instanceof Error ? err.message : err}`, { variant: 'error' });
    }
  };

  const remove = async (target: string) => {
    if (!window.confirm(`Remove admin access for ${target}?`)) return;
    try {
      await removeAdmin.mutateAsync(target);
      enqueueSnackbar(`${target} removed`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Failed: ${err instanceof Error ? err.message : err}`, { variant: 'error' });
    }
  };

  return (
    <Stack spacing={3} className="max-w-xl">
      <div>
        <Typography variant="subtitle2" gutterBottom>
          Built-in admins (from deployment config — not removable here)
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {(data?.envAdmins ?? []).map((e) => (
            <Chip key={e} label={e} variant="outlined" />
          ))}
        </Stack>
      </div>

      <div>
        <Typography variant="subtitle2" gutterBottom>
          Granted admins
        </Typography>
        <Stack spacing={1}>
          {(data?.tableAdmins ?? []).map((e) => (
            <Stack key={e} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Chip label={e} />
              <IconButton size="small" onClick={() => remove(e)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          {(data?.tableAdmins ?? []).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              None granted via the table yet.
            </Typography>
          )}
        </Stack>
      </div>

      <Stack direction="row" spacing={1}>
        <TextField
          label="Add admin (email)"
          size="small"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1"
        />
        <Button variant="contained" onClick={add} disabled={addAdmin.isPending || !email.trim()}>
          Add
        </Button>
      </Stack>
    </Stack>
  );
}
